// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject, type WheelEvent as ReactWheelEvent } from 'react';

import type { ModelEditOp, SolidOpening, SolidWall } from '../../api/types';
import type { GeometriEditor, GestureStatus } from '../../app/bygning/GeometriEditorContext';
import { kindOf } from '../../app/keyTargets';
import { CadIcon } from '../../components/bygning/rediger/cadIcons';
import type { GeometriModel } from '../../bygning/geometri';
import { applyOps, elementTitle, lengthInput, parseLength } from '../../bygning/geometriEdit';
import { flushKeys, resolveKeys } from '../../bygning/geometriKeys';
import {
  alignOpening,
  createOpeningOps,
  dimOffset,
  flipOpeningOps,
  gapOps,
  moveOpeningOps,
  onHost,
  openingTrackPoints,
  optionsTypeName,
  placeOpening,
  placementDims,
  resizeOpeningOps,
  selectedPlacement,
  type OpeningDim,
  type OpeningEdit,
  type OpeningKind,
  type Placement,
  type PlaceSpec,
} from '../../bygning/openingPlace';
import { readSwing } from '../../bygning/openingSwing';
import { planWallSet, type PlanOpeningShape, type XY } from '../../bygning/planWalls';
import { type Track, type TrackPoint } from '../../bygning/smartGuides';
import { wallAt } from '../../bygning/wallDraw';
import type { ViewportHandle } from '../../viewport/Viewport';
import { DimInput, TrackLines } from './GeometriLayer';
import { useSmartTracking } from './useSmartTracking';
import styles from './GeometriLayer.module.css';
import own from './OpeningLayer.module.css';

/** A wall this close to the pointer (px, beyond its faces) hosts the opening. */
const HOST_PX = 14;
/** Smart guides' reach, px (the inference engine's). */
const SNAP_PX = 12;
/** A press that moves less than this (px) is a click. */
const DRAG_PX = 4;

export interface OpeningLayerProps {
  viewport: RefObject<ViewportHandle | null>;
  host: RefObject<HTMLElement | null>;
  editor: GeometriEditor;
  storeyId: number | null;
  z: number;
}

const NOUN: Record<OpeningKind, string> = { door: 'dør', window: 'vindue', opening: 'åbning' };
const LABEL: Record<OpeningKind, string> = { door: 'Dør', window: 'Vindue', opening: 'Åbning' };
const NEW_LABEL: Record<OpeningKind, string> = { door: 'Ny dør', window: 'Nyt vindue', opening: 'Ny åbning' };
const fmt = (v: number) => lengthInput(v);
const isOpeningTool = (t: string): t is OpeningKind => t === 'door' || t === 'window' || t === 'opening';

/** The pointer over the plan while a tool places: where, and the wall it hosts on. */
interface Hover {
  raw: XY;
  host: SolidWall | null;
  /** A smart guide moved the pointer along the host: level with this point (frontend-u0o.12). */
  align?: { tip: string; track: Track } | null;
}

/** The measurement box: a typed distance from the neighbour on one side. */
interface Typed {
  from: 'left' | 'right';
  text: string;
}

/** A grip drag on a selected opening: its middle (slide / re-host) or a jamb (width). */
interface Drag {
  grip: 'move' | 'start' | 'end';
  guid: string;
  /** Middle minus the press point, so the opening does not jump to the pointer. */
  grab: XY;
  moved: boolean;
  raw: XY | null;
  result: OpeningEdit | null;
  align?: { tip: string; track: Track } | null;
}

/**
 * Where a box goes for placement `p`: centred on the opening, `px` past
 * its dimension line (on the side away from the swing), growing away from
 * the wall whichever way the wall runs on screen.
 */
function beyond(p: Placement, toScreen: (q: XY) => [number, number] | null, px: number, minOffset: number): { transform: string } | null {
  const k = dimOffset(p, minOffset);
  const mid = (p.s0 + p.s1) / 2;
  const a = toScreen(onHost(p.host, mid));
  const b = toScreen(onHost(p.host, mid, k));
  if (!a || !b) return null;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const L = Math.hypot(dx, dy) || 1;
  const ux = dx / L;
  const uy = dy / L;
  const x = b[0] + ux * px;
  const y = b[1] + uy * px;
  const shift = Math.abs(uy) >= Math.abs(ux) ? (uy > 0 ? 'translate(-50%, 0)' : 'translate(-50%, -100%)') : ux > 0 ? 'translate(0, -50%)' : 'translate(-100%, -50%)';
  return { transform: `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) ${shift}` };
}

/** The symbol of one opening (`planWallSet`'s, the plan's own) as it would be after `ops`. */
function previewShape(model: GeometriModel, ops: readonly ModelEditOp[], guid: string): { shape: PlanOpeningShape; host: SolidWall } | null {
  const after = applyOps(model, ops);
  const o = after.openings?.find((x) => x.guid === guid);
  const host = o ? after.walls?.find((w) => w.guid === o.wall_guid) : undefined;
  if (!o || !host) return null;
  const shape = planWallSet([host], [o], host.storey_id).openings[0];
  return shape ? { shape, host } : null;
}

/**
 * Rediger › Geometri's openings on the locked plan (frontend-u0o.5, slice
 * S5; mock 03), beside `GeometriLayer` and `CadToolLayer`:
 *
 *  - the Dør (D), Vindue (N) and Åbning (O) tools: hovering a wall previews
 *    the opening hosted on it — slid along to fit between its neighbours,
 *    with Revit's temporary dimensions to them and the measurement box
 *    (digits: the distance from the left neighbour, Tab: from the right);
 *    a door swings to the pointer's side, Space flips it, H its hand. A
 *    click (or Enter after typing) places it; a place it cannot go says why
 *    and is refused;
 *  - a selected opening: its middle grip slides it along the host or, let go
 *    over another wall, re-hosts it; its jamb grips set the width; its
 *    temporary dimensions take a typed gap or width; a door's flip controls
 *    (and Space / H) flip its facing and hand.
 */
export function OpeningLayer({ viewport, host: hostEl, editor, storeyId, z }: OpeningLayerProps) {
  const [, setTick] = useState(0);
  const layerRef = useRef<HTMLDivElement>(null);
  const [hover, setHoverState] = useState<Hover | null>(null);
  const [flip, setFlip] = useState(false);
  const [typed, setTypedState] = useState<Typed | null>(null);
  const [drag, setDragState] = useState<Drag | null>(null);
  const [editDim, setEditDim] = useState<OpeningDim['id'] | null>(null);
  const live = useRef({ editor, storeyId, z, hover, typed, flip, drag });
  live.current = { editor, storeyId, z, hover, typed, flip, drag };
  const setHover = (h: Hover | null) => {
    live.current.hover = h;
    setHoverState(h);
  };
  const setTyped = (t: Typed | null) => {
    live.current.typed = t;
    setTypedState(t);
  };
  const setDrag = (d: Drag | null) => {
    live.current.drag = d;
    setDragState(d);
  };

  const model = editor.model;
  const tool = editor.tool;
  const kind = isOpeningTool(tool) && editor.canEdit && !editor.modify ? tool : null;
  const opts = kind ? editor.openingOptions[kind] : null;

  // ------------------------------------------------------- projection --
  useEffect(() => {
    let frameId: number | null = null;
    const bump = () => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        setTick((t) => t + 1);
      });
    };
    const off = viewport.current?.onCameraChange(bump) ?? (() => {});
    bump();
    return () => {
      off();
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [viewport]);

  const toScreen = useCallback(
    (p: XY): [number, number] | null => {
      const s = viewport.current?.projectWorld(new Float64Array([p[0], p[1], live.current.z]), { clip: false });
      return s && Number.isFinite(s[0]) && Number.isFinite(s[1]) ? [s[0], s[1]] : null;
    },
    [viewport],
  );
  const metresPer = useCallback(
    (px: number): number => {
      const a = viewport.current?.planPoint(0, 0, live.current.z);
      const b = viewport.current?.planPoint(px, 0, live.current.z);
      return a && b ? Math.hypot(b[0] - a[0], b[1] - a[1]) : 0.1;
    },
    [viewport],
  );
  const planAtClient = useCallback(
    (clientX: number, clientY: number): XY | null => {
      const r = layerRef.current?.getBoundingClientRect();
      if (!r) return null;
      const hit = viewport.current?.planPoint(clientX - r.left, clientY - r.top, live.current.z);
      return hit ? [hit[0], hit[1]] : null;
    },
    [viewport],
  );
  /** The wall on the plan's storey under `p`, within `px` of its faces. */
  const wallUnder = useCallback(
    (p: XY, px: number): SolidWall | null => wallAt((live.current.editor.model.walls ?? []) as SolidWall[], p, live.current.storeyId, undefined, metresPer(px))?.wall ?? null,
    [metresPer],
  );

  // ---------------------------------------------------- smart guides --
  const { tracked, points: trackedNow, observe } = useSmartTracking(kind ?? 'grips');
  /** The pointer at `raw` over `host`, aligned by a smart guide when one is in reach (the middle or a jamb level with a point). */
  const smartHover = useCallback(
    (raw: XY, host: SolidWall | null, width: number, alt: boolean, exclude: string | null): Hover => {
      const ed = live.current.editor;
      const tol = metresPer(SNAP_PX);
      const auto = openingTrackPoints(ed.model, live.current.storeyId, exclude);
      // Resting on a wall end or a jamb acquires it (SketchUp), as in the other tools.
      let near: TrackPoint | null = null;
      for (const q of auto) if (Math.hypot(q.p[0] - raw[0], q.p[1] - raw[1]) <= tol && (!near || Math.hypot(near.p[0] - raw[0], near.p[1] - raw[1]) > Math.hypot(q.p[0] - raw[0], q.p[1] - raw[1]))) near = q;
      observe(near);
      if (!host || alt || !ed.snap.snap || !ed.snap.smart) return { raw, host };
      const a = alignOpening(host, raw, width, [...trackedNow(), ...auto], tol);
      return a ? { raw: a.p, host, align: { tip: a.tip, track: a.track } } : { raw, host };
    },
    [metresPer, observe, trackedNow],
  );

  // ------------------------------------------------- the tool's preview --
  const spec: PlaceSpec | null = kind && opts ? { kind, width: opts.width, height: opts.height, sill: opts.sill } : null;
  const placeAt = useCallback(
    (h: Hover | null, t: Typed | null, fl: boolean) => {
      const ed = live.current.editor;
      const k = isOpeningTool(ed.tool) ? ed.tool : null;
      if (!k || !h?.host) return null;
      const o = ed.openingOptions[k];
      const sp = { kind: k, width: o.width, height: o.height, sill: o.sill };
      const distance = t ? parseLength(t.text) : null;
      return placeOpening(ed.model, h.host, h.raw, sp, {
        side: 'pointer',
        flip: fl,
        hand: o.hand,
        centre: o.centre,
        exact: !!h.align,
        ...(t && distance !== null ? { typed: { from: t.from, distance } } : {}),
      });
    },
    [],
  );
  const placed = kind ? placeAt(hover, typed, flip) : null;
  const placement: Placement | null = placed && 'ok' in placed ? placed.ok : null;
  const placeError = placed && 'error' in placed ? placed.error : null;
  // The live gaps (without the typed value) name the measurement box's neighbours.
  const untyped = kind && typed ? placeAt(hover, null, flip) : placed;
  const neighbours = untyped && 'ok' in untyped ? untyped.ok : placement;

  const place = useCallback(() => {
    const { editor: ed, hover: h, typed: t, flip: fl } = live.current;
    const k = isOpeningTool(ed.tool) ? ed.tool : null;
    if (!k) return;
    if (!h?.host) {
      ed.notify(`Peg på en væg for at sætte ${k === 'window' ? 'et' : 'en'} ${NOUN[k]} i den`);
      return;
    }
    const r = placeAt(h, t, fl);
    if (!r) return;
    if ('error' in r) {
      ed.notify(r.error);
      return;
    }
    const o = ed.openingOptions[k];
    const ops = createOpeningOps(r.ok, { kind: k, width: o.width, height: o.height, sill: o.sill }, ed.newGuid(), optionsTypeName(k, o), k === 'door' ? o.leaves : 1);
    setTyped(null);
    void ed.commit(ops, NEW_LABEL[k]);
  }, [placeAt]);

  // A new tool, or other options, start afresh.
  useEffect(() => {
    setTyped(null);
    setFlip(false);
    setHover(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  // ------------------------------------------------ the tool's pointer --
  useEffect(() => {
    const el = hostEl.current;
    if (!el || !kind) return;
    const hoverAt = (raw: XY, alt: boolean): Hover => {
      const ed = live.current.editor;
      const k = isOpeningTool(ed.tool) ? ed.tool : null;
      const o = k ? ed.openingOptions[k] : null;
      // Centrér places it itself: no guide to follow.
      return smartHover(raw, wallUnder(raw, HOST_PX), o?.width ?? 0, alt || !o || o.centre, null);
    };
    const onMove = (e: PointerEvent) => {
      const raw = planAtClient(e.clientX, e.clientY);
      if (!raw) return;
      setHover(hoverAt(raw, e.altKey));
      live.current.editor.cursor.set(raw);
    };
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 || !(e.target instanceof HTMLCanvasElement)) return;
      e.stopPropagation();
      e.preventDefault();
      const raw = planAtClient(e.clientX, e.clientY);
      if (!raw) return;
      setHover(hoverAt(raw, e.altKey));
      place();
    };
    const onLeave = () => setHover(null);
    el.addEventListener('pointermove', onMove, true);
    el.addEventListener('pointerdown', onDown, true);
    el.addEventListener('pointerleave', onLeave);
    const prevCursor = el.style.cursor;
    el.style.cursor = 'crosshair';
    return () => {
      el.removeEventListener('pointermove', onMove, true);
      el.removeEventListener('pointerdown', onDown, true);
      el.removeEventListener('pointerleave', onLeave);
      el.style.cursor = prevCursor;
    };
  }, [hostEl, kind, planAtClient, wallUnder, place, smartHover]);

  // ------------------------------------------------- the tool's keys --
  useEffect(() => {
    if (!kind) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target instanceof HTMLElement ? e.target : null;
      // The options strip's fields and the palette keep their keys.
      if (target?.closest('input, textarea, select, [role="dialog"], [role="menu"]')) return;
      const { editor: ed, hover: h, typed: t } = live.current;
      const k = isOpeningTool(ed.tool) ? ed.tool : null;
      if (!k) return;
      const stop = () => {
        e.preventDefault();
        e.stopPropagation();
      };
      const plain = !e.metaKey && !e.ctrlKey && !e.altKey;
      if (e.key === ' ' && plain) {
        stop();
        if (k === 'door') setFlip((f) => !f);
        return;
      }
      if ((e.key === 'h' || e.key === 'H') && plain && !e.shiftKey) {
        stop();
        if (k === 'door') ed.setOpeningOptions(k, { hand: ed.openingOptions[k].hand === 'H' ? 'V' : 'H' });
        return;
      }
      if (e.key === 'Escape') {
        stop();
        if (t) setTyped(null);
        else ed.setTool('select');
        return;
      }
      if (e.key === 'Enter') {
        stop();
        if (h?.host) place();
        return;
      }
      if (e.key === 'Tab' && plain) {
        stop();
        if (h?.host) setTyped({ from: (t?.from ?? 'left') === 'left' ? 'right' : 'left', text: t?.text ?? '' });
        return;
      }
      if (e.key === 'Backspace' && plain) {
        stop();
        if (t) setTyped(t.text.length > 1 ? { ...t, text: t.text.slice(0, -1) } : null);
        return;
      }
      if (/^[0-9.,]$/.test(e.key) && plain) {
        stop();
        if (h?.host) setTyped({ from: t?.from ?? 'left', text: (t?.text ?? '') + e.key });
        return;
      }
      // The rest is the editor's (the tools' letters, ⌘Z): a letter acts alone, without waiting for a code.
      const out = resolveKeys({ key: e.key, metaKey: e.metaKey, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey, kind: kindOf(target) }, '');
      const cmds = out.buffer ? [flushKeys(out.buffer)].filter((c) => c !== null) : out.commands;
      const run = cmds.filter((c) => c !== 'escape' && c !== 'cycle' && c !== 'cycle-back' && c !== 'delete');
      if (run.length > 0 || (e.key.length === 1 && plain)) stop();
      for (const c of run) ed.run(c);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [kind, place]);

  // ------------------------------------------- a selected opening --
  const selectedGuid = editor.selection.length === 1 && editor.tool === 'select' && !editor.modify ? editor.selection[0] : null;
  const selected = useMemo(() => {
    if (!selectedGuid) return null;
    const o = (model.openings ?? []).find((x) => x.guid === selectedGuid);
    const h = o ? (model.walls ?? []).find((w) => w.guid === o.wall_guid) : undefined;
    if (!o || !h || (storeyId !== null && h.storey_id !== storeyId)) return null;
    return { o, host: h };
  }, [model, selectedGuid, storeyId]);

  const flipSelected = useCallback((which: 'side' | 'hinge') => {
    const { editor: ed } = live.current;
    const g = ed.selection[0];
    const ops = g ? flipOpeningOps(ed.model, g, which) : [];
    const row = g ? (ed.model.openings ?? []).find((x) => x.guid === g) : undefined;
    if (ops.length === 0 || !row) return;
    void ed.commit(ops, `${which === 'side' ? 'Vend slagretning' : 'Skift hængsel'} · ${elementTitle({ kind: 'opening', row })}`);
  }, []);

  // Space / H on a selected door (Revit's flip controls from the keyboard).
  const selectedDoor = selected?.o.kind === 'door' && editor.canEdit;
  useEffect(() => {
    if (!selectedDoor) return;
    const onKey = (e: KeyboardEvent) => {
      if (live.current.drag || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const target = e.target instanceof HTMLElement ? e.target : null;
      if (target?.closest('input, textarea, select, button, a, [role="dialog"], [role="menu"]')) return;
      if (live.current.editor.gesture.get() !== null) return;
      if (e.key === ' ' || e.key === 'h' || e.key === 'H') {
        e.preventDefault();
        e.stopPropagation();
        flipSelected(e.key === ' ' ? 'side' : 'hinge');
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [selectedDoor, flipSelected]);

  const evaluate = useCallback(
    (d: Drag, raw: XY, alt = false): Drag => {
      const ed = live.current.editor;
      const m = ed.model;
      const o = (m.openings ?? []).find((x) => x.guid === d.guid);
      const h = o ? (m.walls ?? []).find((w) => w.guid === o.wall_guid) : undefined;
      if (!o || !h) return d;
      let result: OpeningEdit;
      let align: Hover['align'] = null;
      if (d.grip === 'move') {
        const over = wallUnder(raw, HOST_PX);
        // Over another wall: re-host there, centred on the pointer; else slide along the host.
        const target = over && over.guid !== h.guid ? over : h;
        const p: XY = target === h ? [raw[0] + d.grab[0], raw[1] + d.grab[1]] : raw;
        const sh = smartHover(p, target, Math.abs(o.s_end - o.s_start), alt, o.guid);
        align = sh.align ?? null;
        result = moveOpeningOps(m, d.guid, sh.raw, target, !!align);
      } else result = resizeOpeningOps(m, d.guid, d.grip, raw);
      return { ...d, raw, result, align };
    },
    [wallUnder, smartHover],
  );

  const startGrip = (grip: Drag['grip'], o: SolidOpening, h: SolidWall, e: ReactPointerEvent) => {
    if (e.button !== 0 || !editor.canEdit) return;
    e.stopPropagation();
    e.preventDefault();
    const press = planAtClient(e.clientX, e.clientY);
    if (!press) return;
    const mid = onHost(h, (o.s_start + o.s_end) / 2);
    const x0 = e.clientX;
    const y0 = e.clientY;
    setDrag({ grip, guid: o.guid, grab: [mid[0] - press[0], mid[1] - press[1]], moved: false, raw: null, result: null });
    const move = (m: PointerEvent) => {
      const cur = live.current.drag;
      if (!cur) return;
      if (!cur.moved && Math.hypot(m.clientX - x0, m.clientY - y0) < DRAG_PX) return;
      const raw = planAtClient(m.clientX, m.clientY);
      if (raw) setDrag(evaluate({ ...cur, moved: true }, raw, m.altKey));
    };
    const end = () => {
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('pointercancel', cancel, true);
      window.removeEventListener('keydown', esc, true);
    };
    const up = () => {
      end();
      const cur = live.current.drag;
      setDrag(null);
      if (!cur?.moved || !cur.result) return;
      if ('error' in cur.result) live.current.editor.notify(cur.result.error);
      else if (cur.result.ops.length > 0) void live.current.editor.commit(cur.result.ops, cur.result.label);
    };
    const cancel = () => {
      end();
      setDrag(null);
    };
    const esc = (k: KeyboardEvent) => {
      if (k.key !== 'Escape') return;
      k.preventDefault();
      k.stopPropagation();
      cancel();
    };
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', cancel, true);
    window.addEventListener('keydown', esc, true);
  };

  // A selection change drops a typed dimension.
  useEffect(() => setEditDim(null), [selectedGuid]);

  // ------------------------------------------------------ status line --
  const dragging = drag?.moved ? drag : null;
  const gesture = useMemo<GestureStatus | null>(() => {
    if (kind) {
      const keys = [
        ...(kind === 'door'
          ? [
              { key: 'Mellemrum', text: 'vend slagretning' },
              { key: 'H', text: 'hængsel' },
            ]
          : []),
        { key: '0–9', text: 'skriv afstand' },
        { key: 'Tab', text: 'anden side' },
        { key: 'Esc', text: typed ? 'ryd' : 'tilbage til Vælg' },
      ];
      const lead = hover?.host ? `${LABEL[kind]} · klik for at placere` : `${LABEL[kind]} · peg på en væg`;
      const readout = placement ? `s ${fmt((placement.s0 + placement.s1) / 2)} på Væg ${placement.host.id}` : null;
      return { hint: { lead, keys }, readout };
    }
    if (dragging && selected) {
      const title = elementTitle({ kind: 'opening', row: selected.o });
      const lead = dragging.grip === 'move' ? `Flyt ${title} · slip over en anden væg for at flytte den dertil` : `Bredde · ${title}`;
      return { hint: { lead, keys: [{ key: 'Esc', text: 'annullér' }] }, readout: null };
    }
    return null;
  }, [kind, typed, hover?.host, placement, dragging, selected]);
  useEffect(() => {
    editor.gesture.set(gesture);
  }, [editor.gesture, gesture]);
  useEffect(() => () => live.current.editor.gesture.set(null), []);

  // ------------------------------------------------------------- draw --
  // The dimension line keeps clear of the wall's grips however far out the plan is zoomed.
  const dimMin = metresPer(26);
  const poly = (pts: readonly XY[]): string | null => {
    const out: string[] = [];
    for (const p of pts) {
      const s = toScreen(p);
      if (!s) return null;
      out.push(`${s[0].toFixed(1)},${s[1].toFixed(1)}`);
    }
    return out.join(' ');
  };
  const outline = (w: SolidWall): string | null => {
    const L = Math.hypot(w.end[0] - w.start[0], w.end[1] - w.start[1]);
    if (!(L > 1e-9)) return null;
    const h = Math.max((w.thickness ?? 0) / 2, 0.01);
    const n: XY = [(-(w.end[1] - w.start[1]) / L) * h, ((w.end[0] - w.start[0]) / L) * h];
    return poly([
      [w.start[0] + n[0], w.start[1] + n[1]],
      [w.end[0] + n[0], w.end[1] + n[1]],
      [w.end[0] - n[0], w.end[1] - n[1]],
      [w.start[0] - n[0], w.start[1] - n[1]],
    ]);
  };
  const symbol = (shape: PlanOpeningShape) => {
    const cut = poly(shape.cut);
    return (
      <g className={own.symbol}>
        {cut && <polygon points={cut} className={own.cut} />}
        {shape.leaves.map((l, i) => {
          const d = poly(l);
          return d ? <polyline key={`l${i}`} points={d} className={own.leaf} /> : null;
        })}
        {shape.swings.map((a, i) => {
          const d = poly(a);
          return d ? <polyline key={`s${i}`} points={d} className={own.swing} /> : null;
        })}
        {shape.panes.map((p, i) => {
          const d = poly(p);
          return d ? <polyline key={`p${i}`} points={d} className={own.pane} /> : null;
        })}
      </g>
    );
  };
  const dimLines = (dims: OpeningDim[]) =>
    dims.map((dim) => {
      const a = toScreen(dim.from);
      const b = toScreen(dim.to);
      if (!a || !b || dim.value < 1e-6) return null;
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const L = Math.hypot(dx, dy) || 1;
      const nx = (-dy / L) * 4;
      const ny = (dx / L) * 4;
      return (
        <g key={dim.id} className={styles.dim}>
          <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} />
          <line x1={a[0] - nx} y1={a[1] - ny} x2={a[0] + nx} y2={a[1] + ny} />
          <line x1={b[0] - nx} y1={b[1] - ny} x2={b[0] + nx} y2={b[1] + ny} />
        </g>
      );
    });
  /** A gap's figure needs this much room on screen (px); the width's always shows. */
  const shows = (dim: OpeningDim) => {
    if (dim.value < 1e-6) return false;
    if (dim.id === 'width') return true;
    const a = toScreen(dim.from);
    const b = toScreen(dim.to);
    return a !== null && b !== null && Math.hypot(b[0] - a[0], b[1] - a[1]) >= 34;
  };
  const at = (p: XY, dx = 0, dy = 0) => {
    const s = toScreen(p);
    return s ? { transform: `translate(${(s[0] + dx).toFixed(1)}px, ${(s[1] + dy).toFixed(1)}px) translate(-50%, -50%)` } : null;
  };

  // The forward of a wheel turn over a grip or a figure: the plan zooms wherever the pointer is.
  const forwardWheel = (e: ReactWheelEvent) => {
    const canvas = hostEl.current?.querySelector('canvas');
    if (!canvas || e.target === canvas) return;
    canvas.dispatchEvent(new WheelEvent('wheel', { deltaX: e.deltaX, deltaY: e.deltaY, deltaMode: e.deltaMode, clientX: e.clientX, clientY: e.clientY, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, bubbles: true, cancelable: true }));
  };

  // ---- the tool --
  if (kind && spec && opts) {
    const h = hover?.host ?? null;
    const shown = placement ? previewShape(model, createOpeningOps(placement, spec, 'preview', null, kind === 'door' ? opts.leaves : 1), 'preview') : null;
    const dims = placement ? placementDims(placement, dimMin) : [];
    const pointerAt = hover ? toScreen(hover.raw) : null;
    const from = typed?.from ?? 'left';
    const neighbour = neighbours ? (from === 'left' ? neighbours.left : neighbours.right) : null;
    const liveGap = placement ? (from === 'left' ? placement.gapLeft : placement.gapRight) : null;
    const hostOutline = h ? outline(h) : null;
    // The measurement box sits past the dimensions, on their side of the wall, clear of the preview and its figures.
    const boxAt = placement ? beyond(placement, toScreen, 14, dimMin) : null;
    return (
      <div ref={layerRef} className={styles.layer} onWheel={forwardWheel}>
        <svg className={styles.svg} aria-hidden="true">
          {hostOutline && <polygon points={hostOutline} className={placeError ? own.hostWarn : own.host} />}
          {shown && symbol(shown.shape)}
          {dimLines(dims)}
          <TrackLines tracks={placement && hover?.align ? [hover.align.track] : null} points={tracked} toScreen={toScreen} />
        </svg>
        {dims.map((dim) => {
          const style = shows(dim) ? at(dim.at) : null;
          if (!style) return null;
          const live = typed && dim.id === typed.from;
          return (
            <span key={dim.id} className={live ? styles.liveDim : styles.dimStatic} style={style}>
              {fmt(dim.value)}
            </span>
          );
        })}
        {pointerAt && placement && hover?.align && !typed && <AlignTip tip={hover.align.tip} at={pointerAt} />}
        {pointerAt && h && placeError && (
          <span className={`${styles.tip} ${styles.tipWarn}`} style={{ transform: `translate(${(pointerAt[0] + 14).toFixed(1)}px, ${(pointerAt[1] - 34).toFixed(1)}px)` }} role="status">
            {placeError}
          </span>
        )}
        {boxAt && h && neighbour && (
          <span className={styles.measure} style={boxAt} role="status">
            <span className={styles.measureBox}>
              <small>Afstand fra {neighbour.label}</small>
              <b>
                {typed?.text ? typed.text : <span className={styles.measureLive}>{liveGap !== null ? fmt(liveGap) : ''}</span>}
                <u aria-hidden="true" /> m
              </b>
            </span>
            <span className={styles.measureCap}>Væg {h.id} · skriv for at placere præcist · Tab anden side</span>
          </span>
        )}
      </div>
    );
  }

  // ---- a selected opening --
  if (!selected) return <div ref={layerRef} className={styles.layer} />;
  const { o, host: h } = selected;
  const result = dragging?.result ?? null;
  const ok = result && 'ops' in result ? result : null;
  const shown = ok && ok.ops.length > 0 ? previewShape(model, ok.ops, o.guid) : null;
  const previewModel = shown ? applyOps(model, ok!.ops) : model;
  const cur = selectedPlacement(previewModel, o.guid);
  const dims = cur ? placementDims(cur, dimMin) : [];
  const shownHost = shown?.host ?? h;
  const s0 = cur?.s0 ?? Math.min(o.s_start, o.s_end);
  const s1 = cur?.s1 ?? Math.max(o.s_start, o.s_end);
  const mid = onHost(shownHost, (s0 + s1) / 2);
  const sw = readSwing(o);
  // Past the dimension line, away from the swing, clear of the grips at any zoom.
  const flipsAt = o.kind === 'door' && cur ? beyond(cur, toScreen, 14, dimMin) : null;
  const pointerAt = dragging?.raw ? toScreen(dragging.raw) : null;
  const dragError = result && 'error' in result ? result.error : null;
  const targetWall = dragging?.grip === 'move' && shown && shown.host.guid !== h.guid ? shown.host : null;
  const targetOutline = targetWall ? outline(targetWall) : null;
  const along = (s: number) => onHost(shownHost, s);
  const canEdit = editor.canEdit;

  return (
    <div ref={layerRef} className={styles.layer} onWheel={forwardWheel}>
      <svg className={styles.svg} aria-hidden="true">
        {targetOutline && <polygon points={targetOutline} className={own.host} />}
        {shown && symbol(shown.shape)}
        {!editDim && dimLines(dims)}
        {dragging && <TrackLines tracks={dragging.align && !dragError ? [dragging.align.track] : null} points={tracked} toScreen={toScreen} />}
        {canEdit &&
          (['start', 'end'] as const).map((g) => {
            const s = toScreen(along(g === 'start' ? s0 : s1));
            if (!s) return null;
            const on = drag?.grip === g;
            return (
              <rect
                key={g}
                x={s[0] - 4.5}
                y={s[1] - 4.5}
                width={9}
                height={9}
                className={on ? `${styles.grip} ${styles.gripOn}` : styles.grip}
                onPointerDown={(e) => startGrip(g, o, h, e)}
              >
                <title>Træk karmen: bredde</title>
              </rect>
            );
          })}
        {canEdit &&
          (() => {
            const s = toScreen(mid);
            const ahead = toScreen(onHost(shownHost, (s0 + s1) / 2 + 1));
            if (!s) return null;
            const ux = ahead ? ahead[0] - s[0] : 1;
            const uy = ahead ? ahead[1] - s[1] : 0;
            const ul = Math.hypot(ux, uy) || 1;
            const ax = (ux / ul) * 18;
            const ay = (uy / ul) * 18;
            const on = drag?.grip === 'move';
            return (
              <g>
                <path className={styles.arrow} d={`M${s[0] + ax * 0.55} ${s[1] + ay * 0.55}L${s[0] + ax} ${s[1] + ay}M${s[0] - ax * 0.55} ${s[1] - ay * 0.55}L${s[0] - ax} ${s[1] - ay}`} />
                <rect
                  x={s[0] - 5}
                  y={s[1] - 5}
                  width={10}
                  height={10}
                  transform={`rotate(45 ${s[0]} ${s[1]})`}
                  className={on ? `${styles.grip} ${styles.gripOn}` : styles.grip}
                  onPointerDown={(e) => startGrip('move', o, h, e)}
                >
                  <title>Træk langs væggen, eller slip på en anden væg</title>
                </rect>
              </g>
            );
          })()}
      </svg>
      {!dragging &&
        dims.map((dim) => {
          if (!shows(dim)) return null;
          const style = at(dim.at);
          if (!style) return null;
          const name = dim.id === 'width' ? 'Bredden' : `Afstanden til ${(dim.id === 'left' ? cur!.left : cur!.right).label}`;
          if (editDim === dim.id) {
            return (
              <DimInput
                key={dim.id}
                style={style}
                value={dim.value}
                label={name}
                onCancel={() => setEditDim(null)}
                onCommit={(v) => {
                  setEditDim(null);
                  const ops = gapOps(editor.model, o.guid, dim.id, v);
                  if (ops) void editor.commit(ops, `${dim.id === 'width' ? 'Bredde' : 'Afstand'} · ${elementTitle({ kind: 'opening', row: o })}`);
                  else editor.notify(`Passer ikke: ${fmt(v)} m ${dim.id === 'width' ? 'bred' : 'til naboen'} er der ikke plads til`);
                }}
              />
            );
          }
          return (
            <button
              key={dim.id}
              type="button"
              className={styles.dimLabel}
              style={style}
              disabled={!canEdit}
              title={`${name} — klik for at skrive`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => setEditDim(dim.id)}
            >
              {fmt(dim.value)}
            </button>
          );
        })}
      {dragging && shown && cur && (
        <span className={styles.liveDim} style={at(onHost(shownHost, (s0 + s1) / 2, dimOffset(cur, dimMin))) ?? undefined}>
          {dragging.grip === 'move' ? `${fmt(cur.gapLeft)} · ${fmt(cur.gapRight)}` : fmt(cur.s1 - cur.s0)}
        </span>
      )}
      {dragError && pointerAt && (
        <span className={`${styles.tip} ${styles.tipWarn}`} style={{ transform: `translate(${(pointerAt[0] + 14).toFixed(1)}px, ${(pointerAt[1] - 34).toFixed(1)}px)` }} role="status">
          {dragError}
        </span>
      )}
      {dragging?.align && pointerAt && !dragError && !targetWall && <AlignTip tip={dragging.align.tip} at={pointerAt} />}
      {targetWall && pointerAt && !dragError && (
        <span className={styles.tip} style={{ transform: `translate(${(pointerAt[0] + 14).toFixed(1)}px, ${(pointerAt[1] - 34).toFixed(1)}px)` }}>
          <i className={own.tipDot} aria-hidden="true" />
          Ny vært: Væg {targetWall.id}
        </span>
      )}
      {flipsAt && canEdit && !dragging && (
        <span className={own.flips} style={flipsAt}>
          <button type="button" className={own.flip} title="Vend slagretning (Mellemrum)" aria-label="Vend slagretning (Mellemrum)" onPointerDown={(e) => e.stopPropagation()} onClick={() => flipSelected('side')}>
            <CadIcon name="flipFacing" className={own.flipIcon} />
          </button>
          {sw.leaves === 1 && (
            <button type="button" className={own.flip} title="Skift hængsel (H)" aria-label="Skift hængsel (H)" onPointerDown={(e) => e.stopPropagation()} onClick={() => flipSelected('hinge')}>
              <CadIcon name="flipHand" className={own.flipIcon} />
            </button>
          )}
        </span>
      )}
    </div>
  );
}

/** A smart guide's ScreenTip beside the pointer ("Fra endepunkt · Væg 3"). */
function AlignTip({ tip, at }: { tip: string; at: [number, number] }) {
  return (
    <span className={styles.tip} style={{ transform: `translate(${(at[0] + 14).toFixed(1)}px, ${(at[1] - 34).toFixed(1)}px)` }}>
      <i style={{ background: 'var(--snap-parallel)' }} aria-hidden="true" />
      {tip}
    </span>
  );
}
